import { useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Collapse, Group, Stack, UnstyledButton, Progress } from '@mantine/core';
import Text from '@ui/components/Text/Text';
import type { BackgroundTask, BackgroundTaskStatus } from '../../../models/BackgroundTask';

import BackgroundTaskErrorDescription from './BackgroundTaskErrorDescription';
import BackgroundTaskStatusIndicator from './BackgroundTaskStatusIndicator';

import './BackgroundTaskProgress.scss';

type BackgroundTaskProgressProps = {
	backgroundTask: BackgroundTask,
	testID?: string
};

const COLORS_BY_STATUS: Record<BackgroundTaskStatus, string> = {
	unknown: 'blue.5',
	processing: 'blue.5',
	successful: 'green.4',
	partially_successful: 'orange.3',
	failed: 'red.3',
	canceled: 'gray.4'
};

const BackgroundTaskProgress = ( { backgroundTask, testID }: BackgroundTaskProgressProps ) => {
	const [ showingErrors, setShowingErrors ] = useState( false );

	const color = useMemo( () => COLORS_BY_STATUS[ backgroundTask.status ], [ backgroundTask.status ] );

	return (
		<Stack spacing="12px" className="BackgroundTaskProgress" data-testid={testID}>
			<Group spacing="13px" noWrap p="12px 16px">
				<BackgroundTaskStatusIndicator
					backgroundTask={backgroundTask}
					testID={testID}
					color={color}
				/>
				<UnstyledButton
					disabled={!backgroundTask.hasErrors || !backgroundTask.hasFinished}
					style={{
						pointerEvents: !backgroundTask.hasErrors || !backgroundTask.hasFinished ? 'none' : 'auto',
						flexGrow: 1
					}}
					onClick={() => setShowingErrors( !showingErrors )}
				>
					<Stack spacing="4px">
						<Group position="apart">
							<Text variant="footnote" color="black">{backgroundTask.title}</Text>
							<Text variant="caption-semibold" color="dark.3">{backgroundTask.countText}</Text>
						</Group>
						<Progress
							className="progress-bar"
							value={backgroundTask.progressPercent}
							size="sm"
							color={color}
						/>
					</Stack>
				</UnstyledButton>
			</Group>
			<Collapse in={backgroundTask.hasErrors && showingErrors} bg="gray.0">
				<Stack spacing="4px" p="0 16px" className="errors">
					{backgroundTask.errors.map( ( error, index ) => (
						<BackgroundTaskErrorDescription
							key={error.identifier}
							error={error}
							showBorderBottom={index < backgroundTask.errors.length - 1}
						/>
					) )}
				</Stack>
			</Collapse>
		</Stack>
	);
}

export default observer( BackgroundTaskProgress );
